import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateLandingHeroBannerInput,
    DeleteLandingHeroBannerInput,
    LandingHeroBanner,
    ModelLandingHeroBannerFilterInput,
    UpdateLandingHeroBannerInput
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class LandingHeroBannerService {
    private amplifyClient = generateClient();
    private _cachedBanners: Map<string, LandingHeroBanner[]> = new Map();
    private _isLoading: Map<string, boolean> = new Map();
    private _loadingPromises: Map<
        string,
        Promise<LandingHeroBanner[]>
    > = new Map();

    private _getCacheKey(filter: ModelLandingHeroBannerFilterInput): string {
        return JSON.stringify(filter || {});
    }

    async listLandingHeroBanners(
        filter: ModelLandingHeroBannerFilterInput = null
    ): Promise<LandingHeroBanner[]> {
        const cacheKey = this._getCacheKey(filter);

        if (this._cachedBanners.has(cacheKey)) {
            return this._cachedBanners.get(cacheKey);
        }

        if (
            this._isLoading.get(cacheKey) &&
            this._loadingPromises.has(cacheKey)
        ) {
            return this._loadingPromises.get(cacheKey);
        }

        this._isLoading.set(cacheKey, true);
        const promise = this._fetchBanners(filter, cacheKey);
        this._loadingPromises.set(cacheKey, promise);

        return promise;
    }

    private async _fetchBanners(
        filter: ModelLandingHeroBannerFilterInput,
        cacheKey: string
    ): Promise<LandingHeroBanner[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listLandingHeroBanners,
                    variables: { filter }
                })
            ).data.listLandingHeroBanners.items;

            if (data) {
                const banners = data as LandingHeroBanner[];
                this._cachedBanners.set(cacheKey, banners);
                this._isLoading.set(cacheKey, false);
                this._loadingPromises.delete(cacheKey);
                return banners;
            }
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        } catch (error) {
            console.error(
                '[LandingHeroBanner] listLandingHeroBanners failed:',
                error
            );
            this._isLoading.set(cacheKey, false);
            this._loadingPromises.delete(cacheKey);
            return null;
        }
    }

    clearCache(): void {
        this._cachedBanners.clear();
        this._isLoading.clear();
        this._loadingPromises.clear();
    }

    async getLandingHeroBanner(id: string): Promise<LandingHeroBanner> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getLandingHeroBanner,
                    variables: { id }
                })
            ).data.getLandingHeroBanner;
            if (data) {
                return data as LandingHeroBanner;
            }
        } catch (error) {
            console.error('Error finding LandingHeroBanner:', error);
        }
        return null;
    }

    async createLandingHeroBanner(
        input: CreateLandingHeroBannerInput
    ): Promise<LandingHeroBanner> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createLandingHeroBanner,
                    variables: { input }
                })
            ).data.createLandingHeroBanner;
            if (data) {
                this.clearCache();
                return data as LandingHeroBanner;
            }
        } catch (error) {
            console.error('Error creating LandingHeroBanner:', error);
        }
        return null;
    }

    async updateLandingHeroBanner(
        input: UpdateLandingHeroBannerInput
    ): Promise<LandingHeroBanner> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateLandingHeroBanner,
                    variables: { input }
                })
            ).data.updateLandingHeroBanner;
            if (data) {
                this.clearCache();
                return data as LandingHeroBanner;
            }
        } catch (error) {
            console.error('Error updating LandingHeroBanner:', error);
        }
        return null;
    }

    async deleteLandingHeroBanner(
        input: DeleteLandingHeroBannerInput
    ): Promise<LandingHeroBanner> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteLandingHeroBanner,
                    variables: { input }
                })
            ).data.deleteLandingHeroBanner;
            if (data) {
                this.clearCache();
                return data as LandingHeroBanner;
            }
        } catch (error) {
            console.error('Error deleting LandingHeroBanner:', error);
        }
        return null;
    }
}
